import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createBatchedParamsArrayFromIds, creatorbase, creatorbaseQueries, youtube, youtubeQueries } from '@round/api';
import { useYoutubeChannelsListQueries } from '@round/platforms/youtube';
import { useAllPosts, type AllPostsConfig as Config } from '@round/creatorbase-helpers/api-hooks';
import { isNumber } from 'utility/utility';
import uniq from 'lodash/uniq';
import { getProjectDetailsPostsParams } from './getProjectDetailsPostsParams';

export type YoutubePostsTableRow = creatorbase.YoutubePost & {
    channel: youtube.Channel | null;
};

const CHANNELS_BATCH_SIZE = 50;

const buildChannelBatchParams = (channelIds: number[]) =>
    createBatchedParamsArrayFromIds(channelIds, (ids) => ({ id: ids.join(',') }), CHANNELS_BATCH_SIZE);

const getYoutubeChannelIds = (posts: creatorbase.YoutubePost[]) =>
    uniq(posts.map((post) => post.youtube_details?.account_id).filter(isNumber));

const assembleRows = (
    posts: creatorbase.YoutubePost[],
    channelsById: Map<number, youtube.Channel>
): YoutubePostsTableRow[] =>
    posts.map((post) => {
        const accountId = post.youtube_details?.account_id;
        const channel = isNumber(accountId) ? channelsById.get(accountId) ?? null : null;

        return { ...post, channel };
    });

export default function useProjectDetailsYoutubePosts(campaignId: number, config: Config = { enabled: true }) {
    const postsQuery = useAllPosts(getProjectDetailsPostsParams(campaignId), config);
    const posts = postsQuery.data as creatorbase.YoutubePost[];

    const channelIds = getYoutubeChannelIds(posts);

    const channelsData = useYoutubeChannelsListQueries(
        buildChannelBatchParams(channelIds).map((params) => ({
            params,
            config: { enabled: channelIds.length > 0 },
        })),
        {
            combine: (results) => ({
                byId: new Map(
                    results.flatMap((result) => result.data?.results ?? []).map((channel) => [channel.id, channel])
                ),
                isLoading: results.some((result) => result.isLoading),
            }),
        }
    );

    return {
        data: assembleRows(posts, channelsData.byId),
        isAccountDataLoading: channelsData.isLoading,
        hasError: postsQuery.hasError,
        isLoading: postsQuery.isLoading,
        refresh: postsQuery.refresh,
    };
}

// Imperative twin of the hook above: read-or-fetch a campaign's enriched posts
// straight from the cache. Use it from event handlers (e.g. copy-to-clipboard)
// where a live subscription isn't wanted. Shares params/keys with the hook, so a
// campaign already rendered by the table is a pure cache hit.
export function useEnsureProjectDetailsYoutubePosts() {
    const queryClient = useQueryClient();

    return useCallback(
        async (campaignId: number): Promise<YoutubePostsTableRow[]> => {
            const posts = (await queryClient.ensureQueryData(
                creatorbaseQueries.posts.options.listAll(getProjectDetailsPostsParams(campaignId))
            )) as creatorbase.YoutubePost[];

            const channelIds = getYoutubeChannelIds(posts);

            const channels = await Promise.all(
                buildChannelBatchParams(channelIds).map((params) =>
                    queryClient.ensureQueryData(youtubeQueries.channels.options.list(params))
                )
            );

            const channelsById = new Map(
                channels.flatMap((result) => result.results).map((channel) => [channel.id, channel])
            );

            return assembleRows(posts, channelsById);
        },
        [queryClient]
    );
}
